import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  getSchemaText,
  MixedSchema,
  toMentionContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  SendEmailService,
  SendSMSService,
} from '@principle-theorem/ng-interactions';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { NG_SHARED_CONFIG } from '@principle-theorem/ng-shared';
import {
  addInteractions,
  compileTemplateSchema,
  DEFAULT_ACC_PATIENT_FORMS_CONTENT,
  hasValidEmail,
  hasValidMobile,
  Interaction,
  PatientContextBuilder,
  ScopeDataBuilder,
  stafferToNamedDoc,
  TemplateDefinition,
  toMention,
} from '@principle-theorem/principle-core';
import {
  AccPatientCommunicationType,
  IBrand,
  InteractionType,
  IPatient,
  IPractice,
  IStaffer,
  ITemplateDefinition,
  MentionResourceType,
  TemplateScope,
  TemplateType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  snapshotDefined,
  WithRef,
} from '@principle-theorem/shared';

@Injectable()
export class AccPatientFormsNotificationService {
  private _sharedConfig = inject(NG_SHARED_CONFIG);
  private _org = inject(OrganisationService);
  private _sendSms = inject(SendSMSService);
  private _sendEmail = inject(SendEmailService);
  private _snackBar = inject(MatSnackBar);

  async sendAsSMS(
    practice: WithRef<IPractice>,
    patient: WithRef<IPatient>,
    staffer: WithRef<IStaffer>
  ): Promise<void> {
    const organisation = await snapshotDefined(this._org.organisation$);
    const { region, integrations } = organisation;

    const validMobile = hasValidMobile(
      patient,
      region,
      integrations?.smsProvider
    );

    if (!validMobile) {
      this._snackBar.open(`Invalid patient mobile number`);
      return;
    }

    const contact = toMention(patient, MentionResourceType.Patient);
    const content = await this._getNotificationContent(
      practice,
      patient,
      AccPatientCommunicationType.SMS
    );

    const error = await this._sendSms.sendMessage(
      getSchemaText(content),
      patient,
      staffer.ref,
      practice.ref
    );

    if (error) {
      this._snackBar.open(error.message);
      return;
    }

    const interaction = Interaction.init({
      type: InteractionType.Sms,
      title: [
        toMentionContent(toMention(staffer, MentionResourceType.Staffer)),
        toTextContent(` sent SMS to `),
        toMentionContent(contact),
      ],
      owner: stafferToNamedDoc(staffer),
      content,
    });

    await addInteractions({ contact }, interaction);
    this._snackBar.open('SMS Sent Successfully');
  }

  async sendAsEmail(
    practice: WithRef<IPractice>,
    patient: WithRef<IPatient>
  ): Promise<void> {
    if (!hasValidEmail(patient)) {
      this._snackBar.open(`Invalid patient email address`);
      return;
    }

    const content = await this._getNotificationContent(
      practice,
      patient,
      AccPatientCommunicationType.Email
    );

    const contact = toMention(patient, MentionResourceType.Patient);

    const sendEmailData = {
      subject: `ACC forms from ${practice.name}`,
      content,
      practice,
      fileAttachments: [],
      mediaAttachments: [],
      saveAttachmentsToPatientFiles: false,
    };

    const error = await this._sendEmail.send({ contact }, sendEmailData);

    if (error) {
      this._snackBar.open(error.message);
      return;
    }

    this._snackBar.open('Email Sent Successfully');
  }

  private async _getNotificationContent(
    practice: WithRef<IPractice>,
    patient: WithRef<IPatient>,
    type: AccPatientCommunicationType
  ): Promise<MixedSchema> {
    const templateRef = this._getTemplateRef(practice, type);
    return this._templateContent(patient, practice, templateRef, type);
  }

  private async _templateContent(
    patient: WithRef<IPatient>,
    practice: WithRef<IPractice>,
    templateRef: DocumentReference<ITemplateDefinition> | undefined,
    type: AccPatientCommunicationType
  ): Promise<MixedSchema> {
    const brand = await snapshotDefined(this._org.brand$);
    const scopeData = await ScopeDataBuilder.buildPatientScopeData(
      patient,
      practice,
      this._sharedConfig.appUrl
    );

    const context = new PatientContextBuilder(scopeData).build();
    const template = await this._resolveTemplate(brand.ref, templateRef, type);
    return compileTemplateSchema(template.content, context);
  }

  private _getTemplateRef(
    practice: WithRef<IPractice>,
    type: AccPatientCommunicationType
  ): DocumentReference<ITemplateDefinition> | undefined {
    const isSms = type === AccPatientCommunicationType.SMS;
    return isSms
      ? practice.accSettings?.formNotificationSmsTemplate
      : practice.accSettings?.formNotificationEmailTemplate;
  }

  private async _resolveTemplate(
    ownerScope: DocumentReference<IBrand>,
    templateRef: DocumentReference<ITemplateDefinition> | undefined,
    type: AccPatientCommunicationType
  ): Promise<ITemplateDefinition> {
    if (!templateRef) {
      return this._createDefaultTemplate(ownerScope, type);
    }

    const savedTemplate = await Firestore.getDoc(templateRef);
    return savedTemplate.deleted
      ? this._createDefaultTemplate(ownerScope, type)
      : savedTemplate;
  }

  private _createDefaultTemplate(
    ownerScope: DocumentReference<IBrand>,
    type: AccPatientCommunicationType
  ): ITemplateDefinition {
    const isSms = type === AccPatientCommunicationType.SMS;

    return TemplateDefinition.init({
      name: 'Default ACC Patient Forms Notification',
      content: DEFAULT_ACC_PATIENT_FORMS_CONTENT,
      type: isSms ? TemplateType.PlainText : TemplateType.Html,
      scope: TemplateScope.Patient,
      ownerScope,
    });
  }
}
